import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import {
  Search,
  Filter,
  LayoutGrid,
  List,
  MoveRight,
  Mail,
  Ban,
  CheckCircle,
  RefreshCw,
  FileText,
  X,
  History,
  Clock,
  UserCheck,
  AlertCircle,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import MoveDialog from "./MoveDialog";
import EmailDialog from "./EmailDialog";
import RejectDialog from "./RejectDialog";
import OnboardDialog from "./OnboardDialog";
import ProfileModal from "./ProfileModal";
import { STAGES, VETTING_STAGE_LABELS } from "../../../utils/constants";
import placeholderImage from "../../../assets/placeholder.webp";

const Vetting = () => {
  const {
    vettingBoardCaregivers,
    vettingTableCaregivers,
    vettingPagination,
    getVettingBoard,
    getVettingTable,
    updateVettingStatus,
    updateCareGiverCategory,
    getCaregiverVettingHistory,
    serviceCategories,
  } = useContext(AdminContext);

  const [viewMode, setViewMode] = useState("board");
  const [selectedCaregivers, setSelectedCaregivers] = useState(new Set());
  const isInitialMount = useRef(true);
  // Tracks if filter effects should skip their first fire (initial load handled by viewMode effect)
  const isFilterMount = useRef(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [stageFilter, setStageFilter] = useState("All");

  // Modals
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState(null);

  // Drag & Drop State
  const [draggedId, setDraggedId] = useState(null);

  // Pagination for Table View
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Infinite scroll for Board View
  const [boardPage, setBoardPage] = useState(1);
  const [hasMoreBoard, setHasMoreBoard] = useState(true);
  const [isLoadingBoard, setIsLoadingBoard] = useState(false);

  // Vetting History State
  const [vettingHistory, setVettingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showVettingHistory, setShowVettingHistory] = useState(false);
  const [selectedCaregiverId, setSelectedCaregiverId] = useState(null);

  // Transform board data for UI
  const transformedBoardData = (vettingBoardCaregivers || []).map((cg) => ({
    ...cg,
    id: cg.id,
    avatar: cg.image,
    phone: cg.phoneNumber,
    stage: cg.vettingStatus || cg.vetting_status || "Application_Submitted",
    submittedDate: cg.createdAt
      ? new Date(cg.createdAt).toISOString().split("T")[0]
      : "N/A",
    location: cg.addressLine1 || cg.address?.line1 || "",
    subcity: cg.subcity || cg.address?.line2 || "",
    workPreference:
      cg.workTimePreference || cg.date_time_preference_to_work || "",
    history: cg.vettingHistory || [],
    category: cg.serviceCategory?.label || cg.category || "Uncategorized",
    email: cg.email || "",
    experience: cg.experience || "0",
    nationality: cg.nationality || "Ethiopian",
    languages: Array.isArray(cg.languages)
      ? cg.languages
      : cg.languages
        ? cg.languages.split(",").map((l) => l.trim())
        : ["Amharic", "English"],
    education: cg.education || "Not specified",
    about: cg.about || "No description available",
    rating: Number(cg.rating?.toString() || cg.rating || 0),
    fees: cg.fees || 0,
  }));

  // Transform table data for UI
  const transformedTableData = (vettingTableCaregivers || []).map((cg) => ({
    ...cg,
    id: cg.id,
    avatar: cg.image,
    phone: cg.phoneNumber,
    stage: cg.vettingStatus || cg.vetting_status || "Application_Submitted",
    submittedDate: cg.createdAt
      ? new Date(cg.createdAt).toISOString().split("T")[0]
      : "N/A",
    category: cg.serviceCategory?.label || cg.category || "Uncategorized",
    email: cg.email || "",
    experience: cg.experience || "0",
    nationality: cg.nationality || "Ethiopian",
    languages: Array.isArray(cg.languages)
      ? cg.languages
      : cg.languages
        ? cg.languages.split(",").map((l) => l.trim())
        : ["Amharic", "English"],
    education: cg.education || "Not specified",
    about: cg.about || "No description available",
    rating: cg.rating || 0,
    fees: cg.fees || 0,
  }));

  // Fetch board data
  const fetchBoardData = useCallback(
    async (reset = false) => {
      if (isLoadingBoard) return;

      setIsLoadingBoard(true);
      try {
        const page = reset ? 1 : boardPage;
        await getVettingBoard({
          name: searchTerm,
          category: categoryFilter !== "All" ? categoryFilter : undefined,
          page,
          limit: 20, // Load 20 items at a time for infinite scroll
        });

        if (reset) {
          setBoardPage(2);
          setHasMoreBoard(true);
        } else {
          setBoardPage((prev) => prev + 1);
        }

        // If we get less than limit, no more data
        if (
          (vettingBoardCaregivers || []).length % 20 !== 0 ||
          (vettingBoardCaregivers || []).length === 0
        ) {
          setHasMoreBoard(false);
        }
      } catch (error) {
        toast.error("Failed to load board data");
      } finally {
        setIsLoadingBoard(false);
      }
    },
    [
      getVettingBoard,
      searchTerm,
      categoryFilter,
      boardPage,
      isLoadingBoard,
      (vettingBoardCaregivers || []).length,
    ],
  );

  // Fetch table data
  const fetchTableData = useCallback(async () => {
    try {
      await getVettingTable({
        page: currentPage,
        limit: itemsPerPage,
        name: searchTerm,
        category: categoryFilter !== "All" ? categoryFilter : undefined,
        vettingStatus: stageFilter !== "All" ? stageFilter : undefined,
      });
    } catch (error) {
      toast.error("Failed to load table data");
    }
  }, [
    getVettingTable,
    currentPage,
    itemsPerPage,
    searchTerm,
    categoryFilter,
    stageFilter,
  ]);

  // Initial load and view mode changes — handles first fetch on mount
  useEffect(() => {
    if (viewMode === "board") {
      fetchBoardData(true);
    } else {
      fetchTableData();
    }
  }, [viewMode]);

  // Fetch table data when pagination changes
  useEffect(() => {
    if (viewMode === "table") {
      fetchTableData();
    }
  }, [currentPage, itemsPerPage]);

  // Fetch when filters change — skips first render (viewMode effect already did initial load)
  useEffect(() => {
    if (isFilterMount.current) {
      isFilterMount.current = false;
      return;
    }
    if (viewMode === "table") {
      const timer = setTimeout(() => {
        setCurrentPage(1);
        fetchTableData();
      }, 300);
      return () => clearTimeout(timer);
    } else if (viewMode === "board") {
      const timer = setTimeout(() => {
        fetchBoardData(true);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [searchTerm, categoryFilter, stageFilter]);

  // Infinite scroll handler for board
  const handleBoardScroll = useCallback(
    (e) => {
      if (viewMode !== "board" || isLoadingBoard || !hasMoreBoard) return;

      const container = e.target;
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      // Load more when 80% scrolled
      if (scrollTop + clientHeight >= scrollHeight * 0.8) {
        fetchBoardData();
      }
    },
    [viewMode, isLoadingBoard, hasMoreBoard, fetchBoardData],
  );

  // Filter board data by stage
  const getStageItems = (stage) => {
    if (stageFilter === "All" || stageFilter === stage) {
      return transformedBoardData.filter((c) => c.stage === stage);
    }
    return [];
  };

  // Format date function
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "Invalid Date";

      const options = {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      };
      return date.toLocaleDateString("en-US", options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Get time ago for vetting history
  const getTimeAgo = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return formatDate(dateString);
  };

  // Get vetting color class
  const getVettingColor = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
      case "Re_Examination":
      case "Rejected":
        return "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 border border-red-200 dark:border-red-800";
      case "Interview":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
      case "Document_Verification":
        return "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400 border border-yellow-200 dark:border-yellow-800";
      case "Guarantee_And_Medical_Test":
        return "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border border-purple-200 dark:border-purple-800";
      case "Get_Exam":
        return "bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-800";
      default:
        return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
    }
  };

  // Get vetting icon
  const getVettingIcon = (status) => {
    switch (status) {
      case "Active":
      case "Onboarded":
        return <CheckCircle size={16} className="text-emerald-500" />;
      case "Re_Examination":
      case "Rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      case "Interview":
        return <UserCheck size={16} className="text-blue-500" />;
      case "Document_Verification":
        return <FileText size={16} className="text-yellow-500" />;
      default:
        return <Clock size={16} className="text-gray-500" />;
    }
  };

  // Get initials from name
  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name[0].toUpperCase();
  };

  // Get avatar color based on name
  const getAvatarColor = (name) => {
    if (!name) return "bg-gray-500";
    const colors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-yellow-500",
      "bg-red-500",
      "bg-purple-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-teal-500",
      "bg-orange-500",
      "bg-cyan-500",
    ];
    const index = name.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Handle viewing vetting history
  const handleViewVettingHistory = async (caregiverId) => {
    setSelectedCaregiverId(caregiverId);
    setLoadingHistory(true);
    try {
      const history = await getCaregiverVettingHistory(caregiverId);
      setVettingHistory(history || []);
      setShowVettingHistory(true);
    } catch (error) {
      toast.error("Failed to fetch vetting history");
    } finally {
      setLoadingHistory(false);
    }
  };

  // --- Handlers ---

  const handleDragStart = (e, id) => {
    setDraggedId(id);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e, targetStage) => {
    e.preventDefault();
    if (draggedId) {
      const originalCaregiver = transformedBoardData.find(
        (c) => c.id === draggedId,
      );
      const originalStage = originalCaregiver?.stage;

      if (!originalCaregiver) return;

      try {
        const response = await updateVettingStatus(draggedId, targetStage);
        if (!response.success) {
          throw new Error("Failed to update");
        }

        toast.success("Vetting status updated");
        fetchBoardData(true); // Refresh board data
      } catch (error) {
        toast.error("Error updating vetting status");
      } finally {
        setDraggedId(null);
      }
    }
  };

  const handleBulkSelect = (id) => {
    const newSet = new Set(selectedCaregivers);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedCaregivers(newSet);
  };

  const handleStageSelect = (stage, isChecked) => {
    const stageItems = getStageItems(stage);
    const newSet = new Set(selectedCaregivers);

    stageItems.forEach((item) => {
      if (isChecked) {
        newSet.add(item.id);
      } else {
        newSet.delete(item.id);
      }
    });
    setSelectedCaregivers(newSet);
  };

  const openActionModal = (type, data) => {
    if (data) {
      // Single action
      setSelectedCaregivers(new Set([data.id]));
      setModalData(data);
    } else if (selectedCaregivers.size === 0) {
      toast.info("Please select at least one caregiver");
      return;
    }
    setActiveModal(type);
  };

  const executeAction = async (actionType, payload) => {
    const selectedIds = Array.from(selectedCaregivers);
    try {
      if (actionType === "move") {
        const promises = selectedIds.map((id) =>
          updateVettingStatus(id, payload.stage, payload.note),
        );
        await Promise.all(promises);
        toast.success("Selected caregivers moved successfully");
      } else if (actionType === "reject") {
        const promises = selectedIds.map((id) =>
          updateVettingStatus(id, "Rejected", payload.reason),
        );
        await Promise.all(promises);
        toast.success("Selected caregivers rejected successfully");
      } else if (actionType === "onboard") {
        const statusPromises = selectedIds.map((id) =>
          updateVettingStatus(id, "Onboarded", payload.notes || ""),
        );
        await Promise.all(statusPromises);
        toast.success("Selected caregivers onboarded successfully");
      } else if (actionType === "email") {
        // Email sending logic
        toast.success("Emails sent successfully");
      }

      // Refresh data based on current view
      if (viewMode === "board") {
        fetchBoardData(true);
      } else {
        fetchTableData();
      }
    } catch (error) {
      toast.error(`Error performing ${actionType}`);
    } finally {
      setActiveModal(null);
      setSelectedCaregivers(new Set());
      setModalData(null);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setCategoryFilter("All");
    setStageFilter("All");
    setCurrentPage(1);

    if (viewMode === "board") {
      fetchBoardData(true);
    } else {
      fetchTableData();
    }
  };

  // --- Render Board View ---
  const renderBoard = () => (
    <div
      className="flex gap-4 overflow-x-auto pb-4 h-full items-start custom-scrollbar"
      onScroll={handleBoardScroll}
    >
      {STAGES.map((stage) => {
        const stageItems = getStageItems(stage);
        const isAllSelected =
          stageItems.length > 0 &&
          stageItems.every((item) => selectedCaregivers.has(item.id));

        return (
          <div
            key={stage}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage)}
            className="min-w-[280px] w-[280px] rounded-2xl flex flex-col h-full max-h-full border border-gray-100 dark:border-dark-border shadow-sm 
              bg-background dark:bg-dark-background"
          >
            <div className="p-4 border-b flex justify-between items-center rounded-t-2xl z-10 bg-background dark:bg-dark-background border-gray-200">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={isAllSelected}
                  onChange={(e) => handleStageSelect(stage, e.target.checked)}
                  className="rounded text-primary focus:ring-primary cursor-pointer w-4 h-4"
                />
                <h4 className="font-bold text-sm text-text dark:text-dark-text">
                  {stage}
                </h4>
              </div>
              <span className="bg-card/50 dark:bg-dark-surface/20 text-xs font-bold px-2 py-0.5 rounded-md shadow-sm text-text dark:text-dark-text">
                {stageItems.length}
              </span>
            </div>
            <div className="p-3 space-y-3 overflow-y-auto flex-1 custom-scrollbar scroll-smooth">
              {stageItems.map((item) => (
                <div
                  key={item.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, item.id)}
                  className={`bg-slate-900 p-3 rounded-xl border shadow-sm cursor-grab active:cursor-grabbing hover:shadow-md transition-all ${
                    selectedCaregivers.has(item.id)
                      ? "border-primary ring-1 ring-primary"
                      : "border-slate-800"
                  }`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={selectedCaregivers.has(item.id)}
                        onChange={() => handleBulkSelect(item.id)}
                        className="rounded text-primary focus:ring-primary cursor-pointer"
                      />
                      <span className="text-xs font-bold text-gray-400">
                        {item.id.substring(0, 8)}
                      </span>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleViewVettingHistory(item.id)}
                        className="text-gray-400 hover:text-primary transition-colors"
                        title="Vetting History"
                      >
                        <History size={14} />
                      </button>
                      <button
                        onClick={() => {
                          setModalData(item);
                          setActiveModal("profile");
                        }}
                        className="text-gray-400 hover:text-primary transition-colors"
                        title="View Profile"
                      >
                        <FileText size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 mb-2">
                    <img
                      src={item.avatar || placeholderImage}
                      className="w-8 h-8 rounded-full object-cover"
                      alt=""
                      onError={(e) => {
                        e.target.src = placeholderImage;
                      }}
                    />
                    <div>
                      <p className="text-sm font-bold text-white leading-tight">
                        {item.name}
                      </p>
                      <p className="text-[10px] text-gray-400">
                        {item.category}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-700">
                    <div className="flex gap-1">
                      <button
                        onClick={() => openActionModal("move", item)}
                        title="Move"
                        className="p-1 hover:bg-slate-800 rounded text-gray-400"
                      >
                        <MoveRight size={12} />
                      </button>
                      <button
                        onClick={() => openActionModal("email", item)}
                        title="Email"
                        className="p-1 hover:bg-slate-800 rounded text-gray-400"
                      >
                        <Mail size={12} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
              {isLoadingBoard && hasMoreBoard && (
                <div className="text-center py-2">
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  // --- Render Table View ---
  const renderTable = () => (
    <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden flex flex-col h-full">
      <div className="overflow-x-auto flex-1">
        <table className="w-full text-sm text-left">
          <thead className="bg-accent dark:bg-dark-surface text-xs text-gray-500 dark:text-dark-muted uppercase sticky top-0 z-10 backdrop-blur-sm">
            <tr>
              <th className="px-6 py-4 w-10">
                <input
                  type="checkbox"
                  className="rounded"
                  onChange={(e) => {
                    if (e.target.checked) {
                      const allIds = transformedTableData.map(
                        (item) => item.id,
                      );
                      setSelectedCaregivers(new Set(allIds));
                    } else {
                      setSelectedCaregivers(new Set());
                    }
                  }}
                  checked={
                    transformedTableData.length > 0 &&
                    transformedTableData.every((item) =>
                      selectedCaregivers.has(item.id),
                    )
                  }
                />
              </th>
              <th className="px-6 py-4">Caregiver</th>
              <th className="px-6 py-4">Stage</th>
              <th className="px-6 py-4">Category</th>
              <th className="px-6 py-4">Date</th>
              <th className="px-6 py-4 text-center">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-dark-border">
            {transformedTableData.map((item) => (
              <tr
                key={item.id}
                className="hover:bg-accent dark:hover:bg-dark-surface"
              >
                <td className="px-6 py-4">
                  <input
                    type="checkbox"
                    checked={selectedCaregivers.has(item.id)}
                    onChange={() => handleBulkSelect(item.id)}
                    className="rounded text-primary focus:ring-primary cursor-pointer"
                  />
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <img
                      src={item.avatar || placeholderImage}
                      className="w-8 h-8 rounded-full object-cover"
                      alt=""
                      onError={(e) => {
                        e.target.src = placeholderImage;
                      }}
                    />
                    <div>
                      <p className="font-bold text-text dark:text-dark-text">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">
                        {item.email || "N/A"}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`px-2 py-1 rounded-md text-xs font-bold border ${getVettingColor(
                      item.stage,
                    )}`}
                  >
                    {VETTING_STAGE_LABELS[item.stage] || item.stage}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                  {item.category}
                </td>
                <td className="px-6 py-4 text-gray-500 dark:text-dark-muted">
                  {item.submittedDate}
                </td>
                <td className="px-6 py-4">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => handleViewVettingHistory(item.id)}
                      className="p-1.5 hover:bg-accent dark:hover:bg-dark-border rounded text-gray-500 dark:text-dark-muted"
                      title="Vetting History"
                    >
                      <History size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setModalData(item);
                        setActiveModal("profile");
                      }}
                      className="p-1.5 hover:bg-accent dark:hover:bg-dark-border rounded text-gray-500 dark:text-dark-muted"
                      title="View Profile"
                    >
                      <FileText size={16} />
                    </button>
                    <button
                      onClick={() => openActionModal("move", item)}
                      className="p-1.5 hover:bg-accent dark:hover:bg-dark-border rounded text-gray-500 dark:text-dark-muted"
                      title="Move"
                    >
                      <MoveRight size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {transformedTableData.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
            No caregivers found
          </div>
        )}
      </div>
      <div className="p-2 border-t border-gray-100 dark:border-dark-border bg-accent/50 dark:bg-dark-surface/30">
        <Pagination
          currentPage={vettingPagination.page}
          totalPages={vettingPagination.totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={vettingPagination.limit}
          onItemsPerPageChange={(value) => {
            setItemsPerPage(value);
            setCurrentPage(1);
          }}
          totalItems={vettingPagination.total}
        />
      </div>
    </div>
  );

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-8 h-[calc(100vh-100px)] flex flex-col">
      {/* Header & Controls */}
      <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-6 flex-shrink-0">
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Vetting Dashboard
          </h1>
          <p className="text-gray-500 dark:text-dark-muted text-sm">
            Manage caregiver onboarding pipeline.
          </p>
        </div>

        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="bg-card dark:bg-dark-surface p-1 rounded-xl border border-gray-200 dark:border-dark-border flex shadow-sm">
            <button
              onClick={() => {
                setViewMode("board");
                setSelectedCaregivers(new Set());
              }}
              className={`p-2 rounded-lg transition-all ${
                viewMode === "board"
                  ? "bg-primary/10 text-primary"
                  : "text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
              }`}
              title="Board View"
            >
              <LayoutGrid size={18} />
            </button>
            <button
              onClick={() => {
                setViewMode("table");
                setSelectedCaregivers(new Set());
              }}
              className={`p-2 rounded-lg transition-all ${
                viewMode === "table"
                  ? "bg-primary/10 text-primary"
                  : "text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text"
              }`}
              title="Table View"
            >
              <List size={18} />
            </button>
          </div>

          {/* Bulk Actions */}
          {selectedCaregivers.size > 0 && (
            <div className="flex items-center gap-2 animate-in fade-in zoom-in duration-200">
              <span className="text-xs font-bold text-gray-500 dark:text-dark-muted bg-accent dark:bg-dark-border px-2 py-1 rounded-md">
                {selectedCaregivers.size} Selected
              </span>
              <button
                onClick={() => openActionModal("move")}
                className="p-2 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg hover:bg-accent dark:hover:bg-dark-border text-text dark:text-dark-text"
                title="Move Selected"
              >
                <MoveRight size={18} />
              </button>
              <button
                onClick={() => openActionModal("email")}
                className="p-2 bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg hover:bg-accent dark:hover:bg-dark-border text-text dark:text-dark-text"
                title="Email Selected"
              >
                <Mail size={18} />
              </button>
              <button
                onClick={() => openActionModal("onboard")}
                className="p-2 bg-primary/10 border border-primary/20 text-primary rounded-lg hover:bg-primary/20"
                title="Onboard Selected"
              >
                <CheckCircle size={18} />
              </button>
              <button
                onClick={() => openActionModal("reject")}
                className="p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30"
                title="Reject Selected"
              >
                <Ban size={18} />
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="flex flex-wrap gap-4 mb-6 flex-shrink-0">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-dark-muted"
            size={18}
          />
          <input
            type="text"
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 rounded-xl bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text"
          />
        </div>
        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text"
        >
          <option value="All">All Categories</option>
          {(serviceCategories || []).map((category) => (
            <option key={category.id} value={category.id}>
              {category.label}
            </option>
          ))}
        </select>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          className="px-4 py-2.5 rounded-xl bg-card dark:bg-dark-surface border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-primary text-text dark:text-dark-text"
        >
          <option value="All">All Stages</option>
          {STAGES.map((stage) => (
            <option key={stage} value={stage}>
              {stage}
            </option>
          ))}
        </select>
        <button
          onClick={handleResetFilters}
          className="px-3 py-2.5 bg-accent dark:bg-dark-border rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors text-text dark:text-dark-text"
          title="Reset Filters"
        >
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Content Area */}
      <div className="flex-1 overflow-hidden">
        {viewMode === "board" ? renderBoard() : renderTable()}
      </div>

      {/* Modals */}
      <MoveDialog
        isOpen={activeModal === "move"}
        onClose={() => setActiveModal(null)}
        onConfirm={(stage, note) => executeAction("move", { stage, note })}
        currentStage={modalData?.stage || "Application Submitted"}
      />
      <EmailDialog
        isOpen={activeModal === "email"}
        onClose={() => setActiveModal(null)}
        onSend={() => executeAction("email")}
        recipientCount={selectedCaregivers.size}
      />
      <RejectDialog
        isOpen={activeModal === "reject"}
        onClose={() => setActiveModal(null)}
        onConfirm={(reason) => executeAction("reject", { reason })}
      />
      <OnboardDialog
        isOpen={activeModal === "onboard"}
        onClose={() => setActiveModal(null)}
        onConfirm={(notes) => executeAction("onboard", { notes })}
      />
      <ProfileModal
        isOpen={activeModal === "profile"}
        onClose={() => {
          setActiveModal(null);
          setModalData(null);
        }}
        caregiver={modalData}
      />

      {/* Vetting History Modal */}
      {showVettingHistory && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-dark-border flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-text dark:text-dark-text">
                  Vetting History
                </h3>
                <p className="text-sm text-gray-500 dark:text-dark-muted mt-1">
                  Status changes and timeline
                </p>
              </div>
              <button onClick={() => setShowVettingHistory(false)}>
                <X
                  size={20}
                  className="text-gray-400 hover:text-text dark:hover:text-dark-text"
                />
              </button>
            </div>

            <div className="p-6 max-h-[60vh] overflow-y-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
              ) : vettingHistory.length > 0 ? (
                <div className="space-y-4">
                  {vettingHistory.map((history, index) => (
                    <div
                      key={index}
                      className="relative pl-8 pb-6 last:pb-0 border-l border-gray-200 dark:border-gray-700"
                    >
                      <div className="absolute left-0 top-0 -translate-x-1/2 w-4 h-4 rounded-full bg-primary border-4 border-card dark:border-dark-surface"></div>
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-2">
                          <div className="flex items-center gap-2">
                            {getVettingIcon(history.status)}
                            <span
                              className={`px-2 py-1 rounded-full text-xs font-bold ${getVettingColor(
                                history.status,
                              )}`}
                            >
                              {history.status}
                            </span>
                          </div>
                          <span className="text-xs text-gray-500 dark:text-dark-muted">
                            {history.changedAt && (
                              <span className="block sm:inline sm:ml-2 text-xs text-gray-400">
                                {getTimeAgo(history.changedAt)}
                              </span>
                            )}
                          </span>
                        </div>
                        {history.changedBy && (
                          <div className="text-sm text-gray-600 dark:text-dark-muted mb-2">
                            Changed by:{" "}
                            <span className="font-medium text-text dark:text-dark-text">
                              {history.changedBy.name || "Admin"}
                            </span>
                          </div>
                        )}
                        {history.additionalNote && (
                          <div className="text-sm text-gray-600 dark:text-dark-muted">
                            <span className="font-medium">Note:</span>{" "}
                            {history.additionalNote}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500 dark:text-dark-muted">
                  <History
                    size={48}
                    className="mx-auto mb-4 text-gray-300 dark:text-gray-600"
                  />
                  <p className="font-medium">No vetting history available</p>
                  <p className="text-sm mt-1">No status changes recorded yet</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Vetting;
